import { IconButton, Input, LuIcon, Spinner, toast } from "@overmap-ai/blocks"
import { type FormEvent, memo, useCallback, useState } from "react"
import { useMap } from "react-map-gl/mapbox"

import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { MAPBOX_ACCESS_TOKEN } from "../constants"

export interface MapSearchBarProps {
	placeholder: string
	zoom?: number
}

export const MapSearchBar = memo(function MapSearchBar({ placeholder, zoom = 12 }: MapSearchBarProps) {
	const { current: map } = useMap()
	const [query, setQuery] = useState("")
	const [isLoading, setIsLoading] = useState(false)

	const handleSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>) => {
			event.preventDefault()
			if (isLoading) return
			const coordinatesMaybe = query.split(",").map((coordinate) => parseFloat(coordinate.trim())) // [lat: number, lng: number] if the query is a coordinate, otherwise unpredicatable
			// Check if the array has 2 elements, and if the elements are defined (if defined, they MUST be numbers due to parseFloat). If these conditions are not met, the query is not a coordinate.
			if (coordinatesMaybe.length !== 2 || !coordinatesMaybe[0] || !coordinatesMaybe[1]) {
				// If the query is not a coordinate, it could be a named location. Look it up on Mapbox
				setIsLoading(true)
				await fetch(
					`https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${MAPBOX_ACCESS_TOKEN}&limit=1`,
				)
					.then((response) => response.json())
					.then((data) => {
						setIsLoading(false)
						if (!data.features || data.features.length === 0) {
							// TODO: change to showWarning
							toast.error({ title: "No results found", description: "" })
							return
						}

						// If bounds exists, flyToBounds, otherwise flyTo
						if (data.features[0].bbox) {
							const bbox = data.features[0].bbox

							map?.fitBounds([
								[bbox[0], bbox[1]],
								[bbox[2], bbox[3]],
							])
						} else {
							const center = data.features[0].center

							map?.flyTo({
								center,
								zoom,
							})
						}
					})
					.catch(() => {
						setIsLoading(false)
						toast.error({ title: "Unable to search", description: "Check internet connection." })
					})
			} else {
				map?.flyTo({
					center: coordinatesMaybe.toReversed() as [number, number],
					zoom,
				})
			}
		},
		[isLoading, query, map, zoom],
	)

	return (
		<form className="w-full" onSubmit={handleSubmit}>
			<Input.Root size="md" className="w-full" variant="outline" accentColor="base">
				<Input.Slot>
					<LuIcon icon={modelIconMapping.search} />
				</Input.Slot>

				<Input.Field
					type="text"
					placeholder={placeholder}
					value={query}
					onChange={useChangeEventHandler(setQuery)}
				/>

				<Input.Slot>
					{isLoading ? (
						<Spinner />
					) : (
						<IconButton aria-label="search" type="submit" size="sm" variant="ghost" accentColor="base">
							<LuIcon icon={modelIconMapping.arrowRight} />
						</IconButton>
					)}
				</Input.Slot>
			</Input.Root>
		</form>
	)
})
MapSearchBar.displayName = "MapSearchBar"
